import React, { useContext, useEffect, useState } from 'react';
import { Route, Switch, NavLink, useHistory } from 'react-router-dom';
import { LoginCallback, SecureRoute, useOktaAuth } from '@okta/okta-react';
import { ErrorBoundary } from 'react-error-boundary';
import { useTracking } from 'react-tracking';
import { Button, LoadingSpinner } from 'yankees-react-utils';
import { useTranslation } from 'react-i18next';
import SectionContentContainer from './sections/SectionContentContainer';
import Home, { I18nExplode, RequireCurrentUserStore } from './sections/Home';
import { Login, SelectAliasUser } from './sections/Login';
import PostGame from './sections/PostGame';
import VideoPlaylists from './sections/VideoPlaylists';
import Opponent from './sections/Opponent';
import { PitcherStrengths, CatcherStrengths, HitterStrengths } from './sections/Reports';
import MyObjectives from './sections/MyObjectives';
import { BallcapSessions } from './sections/Ballcap';
import MechanicsAndWorkload from './sections/MechanicsAndWorkload';
import { store } from './store';
import usePlayerSelector from './utils/usePlayerSelector';
import { UserReport } from './data';

function ErrorFallback({ resetErrorBoundary, error }) {
    const { t: translate } = useTranslation();
    const { authState: { accessToken } } = useOktaAuth();
    const { PlayerID, FullName, Team } = usePlayerSelector();

    useEffect(() => {
        if (!window.$IsDugoutApp && window.location.host.split('.')[0] === 'players') {
            const report = {
                Type: 8,
                Url: window.location.href,
                UserAgent: window.navigator.userAgent,
                Subject: 'Error occurred in Player Portal',
                ConsoleErrors: [error].join('\n\n===============================================================================\n\n'),
                LoggedInPlayerId: PlayerID,
                LoggedInPlayerName: FullName,
                LoggedInPlayerTeam: Team?.FullName,
                LanguageDisplayed: translate('languageNameEnglish'),
            };

            const fd = new FormData();
            fd.append('UserReport', JSON.stringify(report));
            UserReport.post(accessToken, fd);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <SectionContentContainer>
            <p>{translate('nav.render-error')}</p>
            <NavLink to="/">
                <Button onClick={resetErrorBoundary} variant="primary">{translate('common.back-to-home')}</Button>
            </NavLink>
            <Button onClick={resetErrorBoundary} className="ms-2">{translate('common.try-again')}</Button>
        </SectionContentContainer>
    );
}

const Routes = () => {
    const { t: translate } = useTranslation();
    const { state: { user } } = useContext(store);
    const { PlayerID, TeamID } = usePlayerSelector();
    const history = useHistory();
    const { trackEvent } = useTracking();
    const [isLoadingUser, setIsLoadingUser] = useState(false);
    const UserLoadingCallback = (isLoading) => {
        setIsLoadingUser(isLoading);
    };

    const trackPageView = () => trackEvent({
        Type: 'pageview',
        UserName: user?.UserName,
        PlayerID,
        TeamID,
        Location: `${window.location.pathname}${window.location.search}${window.location.hash}`,
        Action: null,
        Language: translate('languageNameEnglish'), // send the english version of the language to the logs
    });

    const userName = user?.UserName;
    useEffect(() => {
        trackPageView();
        history.listen(trackPageView); // Track all subsequent pageviews
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [history, userName, PlayerID]);

    return (
        <>
            {isLoadingUser && (
                <h4 style={{ margin: '5px' }} className="fw-normal">
                    <LoadingSpinner style={{ marginRight: '10px' }} />{translate('nav.loading-user')}
                </h4>
            )}
            <ErrorBoundary FallbackComponent={ErrorFallback}>
                <Switch>
                    <Route exact path="/login" component={Login} />
                    <Route path="/login/callback" component={LoginCallback} />
                    <SecureRoute path="/login/select-user" component={SelectAliasUser} />
                    <SecureRoute path="/">
                        <RequireCurrentUserStore UserLoadingCallback={UserLoadingCallback}>
                            <SectionContentContainer>
                                <Switch>
                                    <SecureRoute exact path="/" component={Home} />
                                    <SecureRoute path="/post-game/:gameId?" component={PostGame} />
                                    <SecureRoute path="/video-playlists" component={VideoPlaylists} />
                                    <SecureRoute path="/opponent/:teamId" component={Opponent} />
                                    <SecureRoute path="/pitcher-strengths" component={PitcherStrengths} />
                                    <SecureRoute path="/catcher-strengths" component={CatcherStrengths} />
                                    <SecureRoute path="/hitter-strengths" component={HitterStrengths} />
                                    <SecureRoute path="/my-objectives" component={MyObjectives} />
                                    <SecureRoute path="/ballcap" component={BallcapSessions} />
                                    <SecureRoute path="/mechanics-and-workload" component={MechanicsAndWorkload} />
                                    <SecureRoute exact path="/explode" component={I18nExplode} />
                                </Switch>
                            </SectionContentContainer>
                        </RequireCurrentUserStore>
                    </SecureRoute>
                </Switch>
            </ErrorBoundary>
        </>
    );
};

export default Routes;
